import { Outlet } from 'react-router-dom';

/**
 * Default page layout: a single scroll region with the centered, padded
 * content column. Full-height pages (e.g. the Strategist chat) opt out by
 * rendering directly under the shell instead of this layout — they mirror this
 * same full-width scroll region and `max-w-[1440px] px-6` column so every page lines up.
 */
export function StandardLayout() {
  return (
    // The scroll region is the flex child and spans the full width of <main>, so its scrollbar
    // sits at the window's right edge, not at the edge of the 1440px column inside it. Its track
    // is drawn whether or not this page is tall enough to scroll (`overflow-y: scroll`), so the
    // centered column never moves sideways when a page grows tall enough to scroll, and a wide
    // table that runs to this region's edge (useEdgeBleed) always meets the scrollbar at the
    // window's edge. An empty gutter kept for the scrollbar (`scrollbar-gutter: stable`) takes the
    // same room, but on a page too short to scroll it left a strip of page background between the
    // cut table and the window's edge. The track follows the theme (the dark root sets
    // `color-scheme: dark`, index.css). Strategist and Messages, whose panes do the scrolling, keep
    // such a gutter, as wide as this track, so their columns line up with this one. The vertical
    // padding stays on the scroll region: a `sticky top-0` bar (the Media Library's bulk bar) pins
    // that far below the top, as it always has. `data-page-scroller` is how a wide table finds the
    // edge it may run to (useEdgeBleed).
    //
    // A settings page (SettingsTabs, whose box is `data-settings-tabs`) makes the column a flex column
    // at least as tall as the scroll region, which that box grows to fill: its sticky Save footer then
    // rests at the bottom of the content area on a short tab instead of right under the card. Only
    // there, so no other page's column changes (in a flex column a block with `mx-auto` would shrink
    // to its content). A browser without `:has()` keeps the footer under the content.
    <div data-page-scroller="" className="min-h-0 flex-1 overflow-y-scroll py-6">
      <div className="mx-auto w-full max-w-[1440px] px-6 has-[>[data-settings-tabs]]:flex has-[>[data-settings-tabs]]:min-h-full has-[>[data-settings-tabs]]:flex-col">
        <Outlet />
      </div>
    </div>
  );
}
